import React, { useEffect, useRef, useState } from "react";
import Testimonial from "../../../Testimonial/Testimonial";
import "./Internship.css";

const Internship = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    // Set initial container width
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1; // Adjust speed as needed (pixels per frame)

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  const [activeRole, setActiveRole] = useState(0);

  const rolesData = [
    {
      title: "Frontend Development",
      description:
        "Join our UI/UX team to create beautiful, responsive web applications using React, Vue, and modern CSS frameworks. You'll collaborate with designers and backend developers to implement user interfaces.",
      responsibilities: [
        "Develop responsive web components",
        "Implement state management solutions",
        "Optimize application performance",
        "Participate in code reviews",
        "Collaborate with UX/UI designers",
      ],
    },
    {
      title: "Backend Development",
      description:
        "Work on server-side logic, databases, and API development. You'll help build scalable systems that power our applications and services.",
      responsibilities: [
        "Design and develop RESTful APIs",
        "Implement database schemas and queries",
        "Optimize backend performance",
        "Implement authentication and authorization",
        "Work with cloud infrastructure",
      ],
    },
    {
      title: "Full Stack Development",
      description:
        "Get experience with both frontend and backend technologies. You'll work on complete features from database design to user interface implementation.",
      responsibilities: [
        "Develop end-to-end web applications",
        "Integrate frontend with backend APIs",
        "Implement responsive designs",
        "Database design and optimization",
        "Deploy applications to production",
      ],
    },
    {
      title: "Data Science",
      description:
        "Work with large datasets to extract insights and build predictive models. You'll use machine learning to solve real business problems.",
      responsibilities: [
        "Clean and preprocess data",
        "Build and train machine learning models",
        "Create data visualizations",
        "Perform statistical analysis",
        "Deploy models to production",
      ],
    },
    {
      title: "DevOps",
      description:
        "Help build and maintain our CI/CD pipelines, cloud infrastructure, and deployment processes. You'll ensure our systems are reliable and scalable.",
      responsibilities: [
        "Automate deployment processes",
        "Manage cloud infrastructure",
        "Monitor system performance",
        "Implement security best practices",
        "Optimize CI/CD pipelines",
      ],
    },
    {
      title: "Mobile Development",
      description:
        "Create cross-platform mobile applications using React Native. You'll build apps that work seamlessly on both iOS and Android devices.",
      responsibilities: [
        "Develop mobile app features",
        "Ensure cross-platform compatibility",
        "Optimize app performance",
        "Implement responsive mobile UIs",
        "Collaborate with backend teams",
      ],
    },
    {
      title: "UI/UX Design",
      description:
        "Design intuitive and beautiful user interfaces for our web and mobile applications. You'll work closely with developers to bring your designs to life.",
      responsibilities: [
        "Create wireframes and prototypes",
        "Design user interfaces and experiences",
        "Conduct user research and testing",
        "Collaborate with development teams",
        "Maintain design systems and style guides",
      ],
    },
    {
      title: "Quality Assurance",
      description:
        "Ensure our software meets the highest quality standards through rigorous testing and validation processes.",
      responsibilities: [
        "Develop test plans and cases",
        "Execute manual and automated tests",
        "Identify and report bugs",
        "Collaborate with developers on fixes",
        "Perform regression testing",
      ],
    },
    {
      title: "Cloud Engineering",
      description:
        "Work with cloud platforms to build scalable and reliable infrastructure for our applications and services.",
      responsibilities: [
        "Design cloud architecture",
        "Implement infrastructure as code",
        "Optimize cloud resources and costs",
        "Ensure security and compliance",
        "Automate deployment processes",
      ],
    },
    {
      title: "Cyber Security",
      description:
        "Protect our systems and data from security threats through proactive monitoring and implementation of security measures.",
      responsibilities: [
        "Conduct security assessments",
        "Implement security protocols",
        "Monitor for security breaches",
        "Respond to security incidents",
        "Educate team on security best practices",
      ],
    },
    {
      title: "AI/ML Engineering",
      description:
        "Develop artificial intelligence and machine learning solutions to solve complex business problems and improve our products.",
      responsibilities: [
        "Research and implement ML algorithms",
        "Train and optimize models",
        "Deploy AI solutions to production",
        "Collaborate with data engineers",
        "Stay updated on AI advancements",
      ],
    },
    {
      title: "Blockchain Development",
      description:
        "Work on cutting-edge blockchain technologies to develop decentralized applications and smart contracts.",
      responsibilities: [
        "Develop smart contracts",
        "Build decentralized applications",
        "Implement blockchain solutions",
        "Ensure security of blockchain systems",
        "Research new blockchain technologies",
      ],
    },
  ];

  const [selectedRole, setSelectedRole] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleRoleSelect = (index) => {
    setSelectedRole(index);
    setIsMenuOpen(false);
  };

  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const internFeedback = [
    {
      id: 1,
      feedback:
        "The mentorship I received at MK Etech was incredible. I went from junior developer to confident React developer in just 3 months!",
      name: "Priya Sharma",
    },
    {
      id: 2,
      feedback:
        "Working on real client projects gave me the experience I needed to stand out in job interviews. The practical skills I gained were invaluable.",
      name: "Rahul Kapoor",
    },
    {
      id: 3,
      feedback:
        "The collaborative environment and code reviews helped me improve my coding standards significantly. Best internship experience!",
      name: "Anjali Patel",
    },
    {
      id: 4,
      feedback:
        "I got hands-on experience with modern technologies that are actually used in the industry. This internship bridged the gap between college and corporate.",
      name: "Vikram Singh",
    },
    {
      id: 5,
      feedback:
        "The supportive team and regular feedback sessions helped me grow both technically and professionally. Highly recommended!",
      name: "Neha Gupta",
    },
    {
      id: 6,
      feedback:
        "This internship taught me how to work in an agile environment. Daily standups and sprints prepared me for real corporate culture.",
      name: "Arjun Mehta",
    },
    {
      id: 7,
      feedback:
        "The exposure to live client interactions boosted my confidence. Now I can communicate and present my work effectively.",
      name: "Simran Kaur",
    },
    {
      id: 8,
      feedback:
        "I loved the balance of learning and practical work. Every week I could see visible growth in my skills.",
      name: "Rohit Verma",
    },
    {
      id: 9,
      feedback:
        "The internship wasn’t just coding – it included soft skills, teamwork, and leadership activities which made me industry-ready.",
      name: "Sneha Iyer",
    },
    {
      id: 10,
      feedback:
        "After completing this internship, I cracked my first job interview easily. The certificate and experience added huge value to my resume.",
      name: "Karan Malhotra",
    },
    {
      id: 11,
      feedback:
        "The training sessions on React, Node.js, and cloud deployment gave me skills that I now use in my full-time job.",
      name: "Isha Deshmukh",
    },
    {
      id: 12,
      feedback:
        "I never imagined I would get the chance to work directly on projects for international clients as an intern. It was a huge learning curve!",
      name: "Mohammed Ali",
    },
    {
      id: 13,
      feedback:
        "The feedback culture here is amazing. My mentors helped me identify my weak areas and turn them into strengths.",
      name: "Ritika Joshi",
    },
    {
      id: 14,
      feedback:
        "The internship not only sharpened my coding but also taught me project management, documentation, and presentation skills.",
      name: "Saurabh Kulkarni",
    },
    {
      id: 15,
      feedback:
        "By the end of the internship, I had a portfolio with real-world projects that impressed recruiters instantly.",
      name: "Tanvi Chawla",
    },
    {
      id: 16,
      feedback:
        "The learning curve was steep but exciting. I now feel confident working with JavaScript frameworks and APIs.",
      name: "Abhishek Yadav",
    },
    {
      id: 17,
      feedback:
        "One of the best parts was peer learning. Collaborating with other interns made the journey fun and insightful.",
      name: "Meera Nair",
    },
    {
      id: 18,
      feedback:
        "The exposure to version control, deployment, and DevOps pipelines was something I never got in college.",
      name: "Nikhil Bansal",
    },
    {
      id: 19,
      feedback:
        "Weekly workshops and hackathons during the internship kept me motivated and pushed my limits.",
      name: "Shreya Sen",
    },
    {
      id: 20,
      feedback:
        "This internship gave me the confidence to freelance and take on global clients. Truly life-changing experience.",
      name: "Aditya Menon",
    },
    {
      id: 21,
      feedback:
        "I got exposure to client meetings and learned how to gather requirements effectively.",
      name: "Deepak Rathi",
    },
    {
      id: 22,
      feedback:
        "The internship improved my problem-solving approach. I can now break down tasks like a pro.",
      name: "Ananya Pandey",
    },
    {
      id: 23,
      feedback:
        "The project deadlines taught me time management and prioritization skills.",
      name: "Manoj Kumar",
    },
    {
      id: 24,
      feedback:
        "I enjoyed working with cloud platforms. Deploying apps on AWS and Azure was a big achievement.",
      name: "Pooja Singh",
    },
    {
      id: 25,
      feedback:
        "The hands-on Git and GitHub sessions prepared me for collaborative coding environments.",
      name: "Ravi Shankar",
    },
    {
      id: 26,
      feedback:
        "We had fun knowledge-sharing sessions every Friday. It made learning interactive.",
      name: "Kriti Jain",
    },
    {
      id: 27,
      feedback:
        "The exposure to real-world debugging and issue tracking improved my analytical skills.",
      name: "Yash Agarwal",
    },
    {
      id: 28,
      feedback:
        "I learned UI/UX best practices and how to design user-friendly applications.",
      name: "Swati Mishra",
    },
    {
      id: 29,
      feedback:
        "Team presentations boosted my confidence and communication skills.",
      name: "Rohini Das",
    },
    {
      id: 30,
      feedback:
        "The collaborative tools like Jira and Trello made me understand workflow better.",
      name: "Amit Chauhan",
    },
    {
      id: 31,
      feedback:
        "I got a chance to explore full-stack development and now I can build apps end-to-end.",
      name: "Ritu Sharma",
    },
    {
      id: 32,
      feedback:
        "The code review process was strict but extremely helpful for writing clean code.",
      name: "Harsh Vardhan",
    },
    {
      id: 33,
      feedback:
        "I developed leadership qualities while managing a small intern team in one project.",
      name: "Snehal More",
    },
    {
      id: 34,
      feedback:
        "The exposure to testing tools like Jest and Selenium was a bonus for me.",
      name: "Vivek Anand",
    },
    {
      id: 35,
      feedback:
        "The HR sessions on career growth and resume building were very useful.",
      name: "Ankita Roy",
    },
    {
      id: 36,
      feedback:
        "I made long-lasting friendships and professional connections during this internship.",
      name: "Sandeep Kumar",
    },
    {
      id: 37,
      feedback:
        "Every task pushed me out of my comfort zone, and I loved that challenge.",
      name: "Divya Pillai",
    },
    {
      id: 38,
      feedback:
        "I gained hands-on exposure to APIs, authentication, and payment gateway integration.",
      name: "Farhan Sheikh",
    },
    {
      id: 39,
      feedback:
        "The supportive environment made me look forward to work every single day.",
      name: "Rachna Dubey",
    },
    {
      id: 40,
      feedback:
        "This internship transformed me from a student to a professional ready for the IT industry.",
      name: "Gaurav Sharma",
    },
  ];

  // Auto-rotate functionality
  useEffect(() => {
    const rotationInterval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % internFeedback.length);
    }, 5000);

    return () => clearInterval(rotationInterval);
  }, [internFeedback.length]);

  const navigateToFeedback = (index) => {
    setActiveTestimonial(index);
  };

  const showPreviousFeedback = () => {
    setActiveTestimonial((prev) =>
      prev === 0 ? internFeedback.length - 1 : prev - 1
    );
  };

  const showNextFeedback = () => {
    setActiveTestimonial((prev) => (prev + 1) % internFeedback.length);
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    course: "",
    university: "",
    graduationYear: "",
    role: "",
    resume: null,
    coverLetter: "",
    source: "",
  });

  const formrolesData = [
    { title: "Frontend Development" },
    { title: "Backend Development" },
    { title: "Full Stack Development" },
    { title: "UI/UX Design" },
    { title: "Data Science" },
    { title: "Mobile App Development" },
    { title: "Software Development" },
  ];

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "resume") {
      setFormData({ ...formData, resume: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Format the message for WhatsApp
    const message =
      `*New Internship Application*%0A%0A` +
      `*Name:* ${formData.name}%0A` +
      `*Email:* ${formData.email}%0A` +
      `*Phone:* ${formData.phone}%0A` +
      `*Course:* ${formData.course}%0A` +
      `*University:* ${formData.university}%0A` +
      `*Graduation Year:* ${formData.graduationYear}%0A` +
      `*Preferred Role:* ${formData.role}%0A` +
      `*How did you hear about us?:* ${formData.source}%0A` +
      `*Cover Letter:* ${formData.coverLetter}`;

    // Open WhatsApp with pre-filled message
    window.open(`https://wa.me/919155599968?text=${message}`, "_blank");
  };

  return (
    <div>
      <section className="mk-application-form-section">
        <h2 className="mk-section-title-form">Apply for Internship</h2>
        <div className="mk-form-container">
          <form className="mk-internship-form" onSubmit={handleSubmit}>
            <div className="mk-form-row">
              <div className="mk-form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your full name"
                />
              </div>
              <div className="mk-form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div className="mk-form-row">
              <div className="mk-form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="Enter your phone number"
                />
              </div>
              <div className="mk-form-group">
                <label htmlFor="course">Course/Field of Study *</label>
                <input
                  type="text"
                  id="course"
                  name="course"
                  value={formData.course}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Computer Science, Design, etc."
                />
              </div>
            </div>

            <div className="mk-form-row">
              <div className="mk-form-group">
                <label htmlFor="university">University/College *</label>
                <input
                  type="text"
                  id="university"
                  name="university"
                  value={formData.university}
                  onChange={handleChange}
                  required
                  placeholder="Enter your institution name"
                />
              </div>
              <div className="mk-form-group">
                <label htmlFor="graduationYear">
                  Expected Graduation Year *
                </label>
                <input
                  id="graduationYear"
                  name="graduationYear"
                  value={formData.graduationYear}
                  onChange={handleChange}
                  placeholder="e.g. 2021"
                  required
                >
                 
                </input>
              </div>
            </div>

            <div className="mk-form-row">
              <div className="mk-form-group">
                <label htmlFor="role">Preferred Internship Role *</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Role</option>
                  {formrolesData.map((role, index) => (
                    <option key={index} value={role.title}>
                      {role.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mk-form-group">
                <label htmlFor="source">How did you hear about us? *</label>
                <select
                  id="source"
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Source</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="College">College Notice Board</option>
                  <option value="Friend">Friend/Relative</option>
                  <option value="Website">Company Website</option>
                  <option value="Job Portal">Job Portal</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="mk-form-group full-width">
              <label htmlFor="coverLetter">
                Cover Letter (Why should we select you?)
              </label>
              <textarea
                id="coverLetter"
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleChange}
                rows="4"
                placeholder="Briefly explain why you're interested in this internship and what makes you a good candidate..."
              ></textarea>
            </div>

            <div className="mk-form-submit">
              <button type="submit" className="mk-submit-button">
                Submit Application
              </button>
              <p className="mk-form-note">
                After submitting, you'll be redirected to WhatsApp to complete
                the process.
                <p>Also send your Resume pdf file</p>
              </p>
            </div>
          </form>
        </div>
      </section>

      <div className="mk-internship-container">
        <section className="mk-benefits-section">
          <h2 className="mk-section-title-intern">
            Why Choose Our Internship?
          </h2>
          <div className="mk-benefits-grid">
            <div className="mk-benefit-card mk-card-1">
              <div className="mk-benefit-icon">💼</div>
              <h3>Real-World Projects</h3>
              <p>
                Work on meaningful projects that impact our clients and
                business.
              </p>
            </div>
            <div className="mk-benefit-card mk-card-2">
              <div className="mk-benefit-icon">👨‍💻</div>
              <h3>Mentorship</h3>
              <p>Learn from experienced developers who guide your growth.</p>
            </div>
            <div className="mk-benefit-card mk-card-3">
              <div className="mk-benefit-icon">🌐</div>
              <h3>Networking</h3>
              <p>Connect with professionals and build your industry network.</p>
            </div>
            <div className="mk-benefit-card mk-card-4">
              <div className="mk-benefit-icon">🚀</div>
              <h3>Career Opportunities</h3>
              <p>
                Top performers may receive full-time job offers upon completion.
              </p>
            </div>
            <div className="mk-benefit-card mk-card-5">
              <div className="mk-benefit-icon">📜</div>
              <h3>Internship Certificate</h3>
              <p>
                Get a recognized certificate to showcase your skills and
                experience.
              </p>
            </div>
            <div className="mk-benefit-card mk-card-6">
              <div className="mk-benefit-icon">🎯</div>
              <h3>Skill Development</h3>
              <p>
                Enhance both technical and soft skills with hands-on learning.
              </p>
            </div>
            <div className="mk-benefit-card mk-card-7">
              <div className="mk-benefit-icon">🧑‍🤝‍🧑</div>
              <h3>Team Collaboration</h3>
              <p>
                Work in teams, improve communication, and learn agile practices.
              </p>
            </div>
            <div className="mk-benefit-card mk-card-8">
              <div className="mk-benefit-icon">⏱️</div>
              <h3>Flexible Learning</h3>
              <p>
                Manage your time effectively with a balanced and flexible
                schedule.
              </p>
            </div>
            <div className="mk-benefit-card mk-card-9">
              <div className="mk-benefit-icon">🌟</div>
              <h3>Recognition & Rewards</h3>
              <p>
                Outstanding performers are acknowledged with awards and
                appreciation.
              </p>
            </div>
            <div className="mk-benefit-card mk-card-10">
              <div className="mk-benefit-icon">📚</div>
              <h3>Learning Resources</h3>
              <p>
                Access premium learning materials, tutorials, and knowledge
                sessions.
              </p>
            </div>
            <div className="mk-benefit-card mk-card-11">
              <div className="mk-benefit-icon">💡</div>
              <h3>Innovation Exposure</h3>
              <p>
                Work with cutting-edge technologies and innovative solutions.
              </p>
            </div>
            <div className="mk-benefit-card mk-card-12">
              <div className="mk-benefit-icon">🤝</div>
              <h3>Strong Portfolio</h3>
              <p>
                Build an impressive portfolio with real-world projects and
                achievements.
              </p>
            </div>
          </div>
        </section>

        <section className="mk-roles-section">
          <h2 className="mk-section-title-internship">Internship Roles</h2>

          {/* Desktop Role Tabs */}
          <div className="mk-roles-container-desktop">
            {rolesData.map((role, index) => (
              <div
                key={index}
                className={`mk-role-tab ${
                  selectedRole === index ? "mk-tab-active" : ""
                }`}
                onClick={() => setSelectedRole(index)}
              >
                {role.title}
              </div>
            ))}
          </div>

          {/* Mobile Role Dropdown */}
          <div className="mk-roles-container-mobile">
            <button className="mk-roles-dropdown-toggle" onClick={toggleMenu}>
              <span>{rolesData[selectedRole].title}</span>
              <span
                className={`mk-dropdown-arrow ${
                  isMenuOpen ? "mk-arrow-open" : ""
                }`}
              ></span>
            </button>

            {isMenuOpen && (
              <div className="mk-roles-dropdown-menu">
                {rolesData.map((role, index) => (
                  <div
                    key={index}
                    className={`mk-role-dropdown-item ${
                      selectedRole === index ? "mk-dropdown-active" : ""
                    }`}
                    onClick={() => handleRoleSelect(index)}
                  >
                    {role.title}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mk-role-content">
            <h3>{rolesData[selectedRole].title} Intern</h3>
            <p>{rolesData[selectedRole].description}</p>
            <h4>Key Responsibilities:</h4>
            <ul className="mk-role-list">
              {rolesData[selectedRole].responsibilities.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mk-application-section">
          <h2 className="mk-section-title-internship">Application Process</h2>
          <div className="mk-process-steps">
            <div className="mk-process-step mk-step-1">
              <div className="mk-step-number">1</div>
              <h4>Online Application</h4>
              <p>Submit your resume and portfolio through our portal</p>
            </div>
            <div className="mk-process-step mk-step-2">
              <div className="mk-step-number">2</div>
              <h4>Technical Assessment</h4>
              <p>Complete a coding challenge relevant to your role</p>
            </div>
            <div className="mk-process-step mk-step-3">
              <div className="mk-step-number">3</div>
              <h4>Interviews</h4>
              <p>Meet with our team in technical and cultural fit interviews</p>
            </div>
            <div className="mk-process-step mk-step-4">
              <div className="mk-step-number">4</div>
              <h4>Offer</h4>
              <p>Receive your internship offer and welcome package</p>
            </div>
          </div>
        </section>

        <section className="intern-feedback-section">
          <h2 className="feedback-section-heading">What Our Interns Say</h2>

          <div className="feedback-carousel-container">
            <div className="feedback-carousel-track">
              {internFeedback.map((item, index) => (
                <div
                  key={item.id}
                  className={`feedback-item-display ${
                    index === activeTestimonial ? "display-active" : ""
                  }`}
                >
                  <div className="feedback-content-box">
                    <div className="feedback-text-content">{item.feedback}</div>
                    <div className="feedback-author-info">
                      <h5>{item.name}</h5>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation controls */}
            <button
              className="carousel-nav-btn nav-prev"
              onClick={showPreviousFeedback}
            >
              &#10094;
            </button>
            <button
              className="carousel-nav-btn nav-next"
              onClick={showNextFeedback}
            >
              &#10095;
            </button>

            {/* Indicator dots */}
            <div className="carousel-indicators">
              {internFeedback.map((_, index) => (
                <span
                  key={index}
                  className={`indicator-dot ${
                    index === activeTestimonial ? "dot-active" : ""
                  }`}
                  onClick={() => navigateToFeedback(index)}
                ></span>
              ))}
            </div>
          </div>
        </section>
      </div>

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Internship;
